import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPill, ErrorState } from '@ui-kit';
import type { StatusTone } from '@ui-kit';
import { useSagaInstance } from '../hooks/useSagaInstance';
import { useSagaSteps } from '../hooks/useSagaSteps';
import { useResumeSaga } from '../hooks/useResumeSaga';
import { useHasPermission } from '../../_shared/hooks/useCurrentUser';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import { SagaStepTimeline } from './SagaStepTimeline';
import type { SagaStatus } from '../types/sagaMonitor';

interface SagaInstanceDetailDrawerProps {
  instanceId: number | null;
  onClose: () => void;
  onResumed?: (toast: string) => void;
}

function statusTone(status: SagaStatus): StatusTone {
  switch (status) {
    case 'COMPLETED': return 'success';
    case 'COMPENSATED': return 'info';
    case 'FAILED': return 'danger';
    case 'COMPENSATING': return 'warning';
    case 'STARTED': return 'neutral';
    default: return 'neutral';
  }
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Asia/Seoul',
  });
}

function DrawerRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '130px 1fr',
        gap: 'var(--spacing-3)',
        padding: 'var(--spacing-2) 0',
        borderBottom: '1px dashed var(--color-surface-border)',
        fontSize: 'var(--font-size-sm)',
        alignItems: 'flex-start',
      }}
    >
      <span
        style={{
          color: 'var(--color-text-secondary)',
          fontWeight: 500,
          fontSize: 'var(--font-size-xs)',
          paddingTop: 2,
        }}
      >
        {label}
      </span>
      <span style={{ color: 'var(--color-text-primary)', wordBreak: 'break-all' }}>
        {children}
      </span>
    </div>
  );
}

function DrawerSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
      {[1, 2].map((n) => (
        <div
          key={n}
          style={{
            background: 'var(--color-surface-background)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
          }}
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 12,
                width: `${55 + i * 10}%`,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                marginBottom: 'var(--spacing-3)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * SagaInstanceDetailDrawer — 사가 인스턴스 상세 + 단계 타임라인 + 재시작 버튼
 * role=dialog · aria-modal=true · ESC 닫기 · Tab 포커스 트랩
 */
export function SagaInstanceDetailDrawer({
  instanceId,
  onClose,
  onResumed,
}: SagaInstanceDetailDrawerProps) {
  const { t } = useTranslation();
  const detailQuery = useSagaInstance(instanceId);
  const stepsQuery = useSagaSteps(instanceId);
  const resumeMutation = useResumeSaga();
  const canResume = useHasPermission('SAGA_MONITOR_RETRY');

  const closeRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  const [resumeConfirmOpen, setResumeConfirmOpen] = useState(false);
  const [contextExpanded, setContextExpanded] = useState(false);

  const detail = detailQuery.data;
  const steps = stepsQuery.data ?? [];

  // ESC 닫기
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (resumeConfirmOpen) {
          setResumeConfirmOpen(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, resumeConfirmOpen]);

  // 열릴 때 닫기 버튼에 포커스
  useEffect(() => {
    if (instanceId !== null) {
      setTimeout(() => closeRef.current?.focus(), 50);
    }
  }, [instanceId]);

  // Tab 포커스 트랩
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !drawerRef.current) return;
    const focusables = drawerRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  const handleResumeSubmit = useCallback(() => {
    if (!instanceId) return;
    resumeMutation.mutate(instanceId, {
      onSuccess: (data) => {
        setResumeConfirmOpen(false);
        onResumed?.(
          t('sagaMonitor.detail.resume.success', { status: t(`sagaMonitor.status.${data.status}`) })
        );
      },
      onError: () => {
        setResumeConfirmOpen(false);
        onResumed?.(t('sagaMonitor.detail.resume.error'));
      },
    });
  }, [instanceId, resumeMutation, onResumed, t]);

  if (instanceId === null) return null;

  const canResumeInstance = detail?.resumable ?? false;

  // 요약 카드용 — steps 쿼리에서 보상 완료(COMPENSATED)된 단계명만 파생 (BE 상세 응답엔 없음)
  const compensatedStepNames = steps
    .filter((s) => s.status === 'COMPENSATED')
    .sort((a, b) => b.stepOrder - a.stepOrder)
    .map((s) => s.stepName);

  return (
    <>
      {/* 배경 오버레이 */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--color-overlay-backdrop)',
          zIndex: 200,
        }}
      />

      {/* 드로어 패널 */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('sagaMonitor.detail.title')}
        aria-describedby="saga-drawer-desc"
        onKeyDown={handleKeyDown}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(580px, 92vw)',
          background: 'var(--color-surface-muted)',
          borderLeft: '1px solid var(--color-surface-border)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 201,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-4) var(--spacing-6)',
            background: 'var(--color-surface-elevated)',
            borderBottom: '1px solid var(--color-surface-border)',
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            {t('sagaMonitor.detail.title')}
            {detail && (
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-brand-primary)',
                  background: 'color-mix(in srgb, var(--color-brand-primary) 12%, transparent)',
                  padding: '1px var(--spacing-2)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                #{detail.id}
              </span>
            )}
          </h2>
          <button
            ref={closeRef}
            type="button"
            aria-label={t('sagaMonitor.detail.button.close')}
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-xl)',
              cursor: 'pointer',
              width: 32,
              height: 32,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-md)',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-surface-muted)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            ✕
          </button>
        </div>

        {/* 본문 */}
        <div
          id="saga-drawer-desc"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 'var(--spacing-4) var(--spacing-6)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-4)',
          }}
        >
          {/* Loading skeleton */}
          {(detailQuery.isLoading || stepsQuery.isLoading) && <DrawerSkeleton />}

          {/* Error */}
          {detailQuery.isError && (
            <div style={{ padding: 'var(--spacing-2)' }}>
              <ErrorState
                title={t('sagaMonitor.detail.error.loadFailed')}
                message={t('sagaMonitor.error.network')}
                onRetry={() => void detailQuery.refetch()}
              />
            </div>
          )}

          {/* Default — 상세 로드 완료 */}
          {detail && (
            <>
              {/* §1 요약 */}
              <section
                aria-labelledby="saga-drawer-summary"
                style={{
                  background: 'var(--color-surface-background)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--spacing-4)',
                }}
              >
                <h3
                  id="saga-drawer-summary"
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 700,
                    color: 'var(--color-text-primary)',
                    margin: '0 0 var(--spacing-3)',
                  }}
                >
                  {t('sagaMonitor.detail.section.summary')}
                </h3>

                <DrawerRow label={t('sagaMonitor.detail.field.sagaName')}>
                  <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>
                    {detail.sagaName}
                    <span style={{ fontFamily: 'var(--font-family-sans)', fontWeight: 400, color: 'var(--color-text-secondary)' }}>
                      {' '}({detail.sagaNameLabel})
                    </span>
                  </span>
                </DrawerRow>

                <DrawerRow label={t('sagaMonitor.detail.field.status')}>
                  <StatusPill tone={statusTone(detail.status)}>
                    {t(`sagaMonitor.status.${detail.status}`)}
                  </StatusPill>
                </DrawerRow>

                <DrawerRow label={t('sagaMonitor.detail.field.startedAt')}>
                  <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                    {formatDateTime(detail.startedAt)} KST
                  </span>
                </DrawerRow>

                {detail.finishedAt && (
                  <DrawerRow label={t('sagaMonitor.detail.field.finishedAt')}>
                    <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                      {formatDateTime(detail.finishedAt)} KST
                    </span>
                  </DrawerRow>
                )}

                {detail.currentStep && (
                  <DrawerRow label={t('sagaMonitor.detail.field.currentStep')}>
                    <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                      {detail.currentStep}
                    </span>
                  </DrawerRow>
                )}

                {detail.failedStep && (
                  <DrawerRow label={t('sagaMonitor.detail.field.failedStep')}>
                    <span
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-danger)',
                        fontWeight: 600,
                      }}
                    >
                      {detail.failedStep}
                    </span>
                  </DrawerRow>
                )}

                {detail.failureMessage && (
                  <DrawerRow label={t('sagaMonitor.detail.field.failureMessage')}>
                    <span
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-danger)',
                        wordBreak: 'break-all',
                      }}
                    >
                      {detail.failureMessage}
                    </span>
                  </DrawerRow>
                )}

                {compensatedStepNames.length > 0 && (
                  <DrawerRow label={t('sagaMonitor.detail.field.compensatedSteps')}>
                    <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-info)' }}>
                      {compensatedStepNames.join(', ')}
                    </span>
                  </DrawerRow>
                )}
              </section>

              {/* §2 단계 타임라인 */}
              <section
                aria-labelledby="saga-drawer-timeline"
                style={{
                  background: 'var(--color-surface-background)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--spacing-4)',
                }}
              >
                <h3
                  id="saga-drawer-timeline"
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 700,
                    color: 'var(--color-text-primary)',
                    margin: '0 0 var(--spacing-3)',
                  }}
                >
                  {t('sagaMonitor.detail.section.timeline')}
                </h3>
                {stepsQuery.isError ? (
                  <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-danger)' }}>
                    {t('sagaMonitor.detail.error.stepsLoadFailed')}
                  </p>
                ) : (
                  <SagaStepTimeline steps={steps} />
                )}
              </section>

              {/* §3 컨텍스트 JSON (접기/펼치기) */}
              {detail.context && (
                <section
                  aria-labelledby="saga-drawer-context"
                  style={{
                    background: 'var(--color-surface-background)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                  }}
                >
                  <button
                    type="button"
                    id="saga-drawer-context"
                    aria-expanded={contextExpanded}
                    onClick={() => setContextExpanded((v) => !v)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-2)',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 700,
                      color: 'var(--color-text-primary)',
                      textAlign: 'left',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-brand-primary)',
                        display: 'inline-block',
                        transition: 'transform 0.2s',
                        transform: contextExpanded ? 'none' : 'rotate(-90deg)',
                      }}
                    >
                      ▼
                    </span>
                    {t('sagaMonitor.detail.section.context')}
                  </button>
                  {contextExpanded && (
                    <pre
                      style={{
                        margin: 0,
                        padding: 'var(--spacing-4)',
                        background: 'var(--color-surface-elevated)',
                        borderTop: '1px solid var(--color-surface-border)',
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        overflowX: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                      }}
                    >
                      {JSON.stringify(detail.context, null, 2)}
                    </pre>
                  )}
                </section>
              )}
            </>
          )}
        </div>

        {/* 푸터 */}
        <div
          style={{
            padding: 'var(--spacing-3) var(--spacing-6)',
            borderTop: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-elevated)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 'var(--spacing-2)',
            flexShrink: 0,
          }}
        >
          <div>
            {canResume && canResumeInstance && (
              <ButtonGuard requirePermission="SAGA_MONITOR_RETRY">
                {({ disabled, reason }) => (
                  <button
                    type="button"
                    disabled={disabled || resumeMutation.isPending}
                    title={reason}
                    onClick={() => setResumeConfirmOpen(true)}
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-4)',
                      background: disabled || resumeMutation.isPending
                        ? 'var(--color-surface-muted)'
                        : 'var(--color-brand-primary)',
                      color: disabled || resumeMutation.isPending
                        ? 'var(--color-text-muted)'
                        : 'var(--color-text-inverse)',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 600,
                      cursor: disabled || resumeMutation.isPending ? 'not-allowed' : 'pointer',
                      opacity: disabled || resumeMutation.isPending ? 0.6 : 1,
                      fontFamily: 'var(--font-family-sans)',
                    }}
                  >
                    {resumeMutation.isPending ? '...' : t('sagaMonitor.detail.button.resume')}
                  </button>
                )}
              </ButtonGuard>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('sagaMonitor.detail.button.close')}
          </button>
        </div>
      </div>

      {/* 재시작 확인 다이얼로그 */}
      {resumeConfirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="resume-confirm-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.55)',
          }}
        >
          <div
            style={{
              background: 'var(--color-surface-background)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
              width: 420,
              maxWidth: '90vw',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <h2
              id="resume-confirm-title"
              style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--spacing-3)' }}
            >
              {t('sagaMonitor.detail.resume.confirm.title')}
            </h2>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-5)',
              }}
            >
              {t('sagaMonitor.detail.resume.confirm.body')}
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setResumeConfirmOpen(false)}
                disabled={resumeMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                }}
              >
                {t('sagaMonitor.detail.resume.confirm.cancel')}
              </button>
              <button
                type="button"
                onClick={handleResumeSubmit}
                disabled={resumeMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: resumeMutation.isPending ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                  color: resumeMutation.isPending ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: resumeMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: resumeMutation.isPending ? 0.6 : 1,
                }}
              >
                {resumeMutation.isPending ? '...' : t('sagaMonitor.detail.resume.confirm.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </>
  );
}
